import React, { useRef, useState, useCallback, useMemo } from "react";
import { url } from "lib/format";
import withLanguageContext from "lib/hocs/withLanguageContext";
import { IconButton } from "lib/components/Button";

const SearchResultsList = ({
  menuItems,
  courses,
  languageContext,
  urlPrefix,
}) => (
  <ul
    className="list flex flex-space-b column"
    id="searchbar-list"
    role="listbox"
  >
    {menuItems &&
      menuItems.map((a, i) => (
        <li className="list-item flex" key={i}>
          <a
            title={languageContext(a.val.name)}
            href={url(a.val.url)}
            className="menu-expandlist-title"
          >
            <p>{languageContext(a.val.name)}</p>
            {a.parent && <span> {a.parent} </span>}
          </a>{" "}
        </li>
      ))}
    {courses &&
      courses.map((a) => (
        <li className="list-item flex" key={a.id}>
          <a
            title={a.title}
            href={urlPrefix + a.id}
            className="menu-expandlist-title"
          >
            <p>{a.title}</p>
            <span>{languageContext("course")}</span>
          </a>
        </li>
      ))}
  </ul>
);

const SearchBar = (props) => {
  const inputSearchRef = useRef(null);
  const timerRef = useRef(null);
  const { languageContext } = props;
  const [courses, setCourses] = useState(null);
  const [menuItems, setMenuItems] = useState(null);
  const [hasFocus, setHasFocus] = useState(false);

  const searchMap = useMemo(() => {
    const { menu } = props;
    if (!menu) return [];
    return Object.keys(menu).map((menuKey) => {
      const b = menu[menuKey];
      const parent =
        b.parentId &&
        menu[b.parentId] &&
        languageContext(menu[b.parentId].name);
      return {
        name: languageContext(b.name).toLowerCase(),
        val: b,
        parent,
        sParent: (parent || "").toLowerCase(),
      };
    });
  }, [props, languageContext]);

  const search = useCallback(() => {
    const val = (
      inputSearchRef.current && inputSearchRef.current.value
        ? inputSearchRef.current.value
        : ""
    ).toLowerCase();

    if (!val) {
      setCourses(null);
      setMenuItems(null);
      return;
    }

    const filteredCourses = (props.courses || []).filter(
      (a) => a.title.toLowerCase().indexOf(val) >= 0,
    );

    const filteredMenuItems = searchMap.filter(
      (a) =>
        a.val.url && (a.name.indexOf(val) >= 0 || a.sParent.indexOf(val) >= 0),
    );

    setCourses(filteredCourses);
    setMenuItems(filteredMenuItems);
  }, [props.courses, searchMap]);

  const focusOnSearch = useCallback(() => {
    if (inputSearchRef.current) {
      inputSearchRef.current.focus();
    }
  }, []);

  const empty = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (inputSearchRef.current) {
        inputSearchRef.current.value = "";
      }
      setCourses(null);
      setMenuItems(null);
      setHasFocus(false);
    }, 500);
  }, []);

  const dontEmpty = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setHasFocus(true);
  }, []);

  const index = 0;
  const urlPrefix = url("/education/entercourse.aspx?courseid=");

  return (
    <div
      className={`searchbar ${hasFocus ? "focused" : ""}`}
      onBlur={empty}
      onFocus={dontEmpty}
      role="menuitem"
    >
      <span>
        <label htmlFor="searchbar-input" className="sr-only">
          {languageContext("searchinmenu")}
        </label>
        <input
          id="searchbar-input"
          type="text"
          ref={inputSearchRef}
          role="combobox"
          aria-autocomplete="list"
          aria-controls="searchbar-list"
          aria-expanded={Boolean(menuItems && menuItems.length > 0)}
          onKeyUp={search}
          tabIndex={index}
          placeholder={languageContext("searchinmenu")}
          name={languageContext("search")}
        />
        <IconButton
          icon="icon-search"
          label={languageContext("search")}
          onClick={focusOnSearch}
          overridefocus="--colorLeftMenuFocus"
        />
      </span>

      {(menuItems && menuItems.length > 0) ||
      (courses && courses.length > 0) ? (
        <SearchResultsList
          menuItems={menuItems}
          courses={courses}
          languageContext={languageContext}
          urlPrefix={urlPrefix}
        />
      ) : (
        <ul id="searchbar-list" role="listbox" className="hidden">
          <li role="option" className="empty-option" aria-selected="false">
            {languageContext("noresults")}
          </li>
        </ul>
      )}
    </div>
  );
};

export default withLanguageContext(SearchBar, "Menu");
